import { numberSpacing } from "#root/lib/formatting";
import { prepareCanvas } from "../lib/canvas";
import { dateStringToDate } from "../lib/conversions";
import { measureText } from "../lib/layouting";
export async function waterfallChart(canvas, {
  sets,
  points,
  minY,
  maxY
}, {
  axisX,
  axisY
} = {}) {
  const {
    ctx
  } = prepareCanvas(canvas); // render y axis

  let yScale = [minY, minY + (maxY - minY) / 4, minY + (maxY - minY) / 2, minY + (maxY - minY) / 4 * 3, maxY]; // identify value with max amount of digits after the decimal point

  let decimalPlaces = -Infinity;

  for (let i = 0; i < yScale.length; i++) {
    const parts = String(yScale[i]).split(".");

    if (parts.length > 1) {
      decimalPlaces = Math.max(decimalPlaces, parts[1].length);
    } else {
      decimalPlaces = 0;
    }
  }

  if (decimalPlaces > 2) decimalPlaces = 2;

  for (let i = 0; i < yScale.length; i++) {
    const el = document.createElement("div");
    el.classList.add("chart-axis-label");
    el.textContent = numberSpacing(yScale[i], decimalPlaces);
    axisY.appendChild(el);
  }

  var dpi = window.devicePixelRatio || 1;
  var rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpi;
  canvas.height = rect.height * dpi;
  const {
    width,
    height
  } = canvas;
  const columnWidth = width / (points.length * 2);
  let xGridPrecisionMode; // "day", "date", "month", "month/year, "year"

  const timeDifference = dateStringToDate(points[points.length - 1].x).getTime() - dateStringToDate(points[0].x).getTime();

  if (timeDifference <= 1000 * 60 * 60 * 24 * 7) {
    xGridPrecisionMode = "day";
  } else if (timeDifference <= 1000 * 60 * 60 * 24 * 31 * 3) {
    xGridPrecisionMode = "date";
  } else if (timeDifference < 1000 * 60 * 60 * 24 * 365) {
    xGridPrecisionMode = "month";
  } else if (timeDifference < 1000 * 60 * 60 * 24 * 365 * 3) {
    xGridPrecisionMode = "month/year";
  } else {
    xGridPrecisionMode = "year";
  } // render frame around canvas


  ctx.strokeStyle = "#1F1F1F";
  ctx.lineWidth = 1 * dpi;
  ctx.globalAlpha = 1;
  ctx.strokeRect(0, 0, width, height); // 25 50 75 horizontal lines

  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 1 * dpi;
  ctx.globalAlpha = 0.07;
  ctx.beginPath();
  ctx.moveTo(0, height / 4);
  ctx.lineTo(width, height / 4);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, height / 2);
  ctx.lineTo(width, height / 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, height / 4 * 3);
  ctx.lineTo(width, height / 4 * 3);
  ctx.stroke();
  ctx.globalAlpha = 1;
  let keyPoints = [];
  let axisSignificantMarkers = [];

  function addKeyPoint(pointIndex, value, numericTag) {
    const date = dateStringToDate(value.x);
    let tag;

    if (xGridPrecisionMode === "day") {
      tag = date.toLocaleDateString(undefined, {
        weekday: "long"
      });
    } else if (xGridPrecisionMode === "date") {
      tag = date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric"
      });
    } else if (xGridPrecisionMode === "month") {
      tag = date.toLocaleDateString(undefined, {
        month: "long"
      });
    } else if (xGridPrecisionMode === "month/year") {
      tag = date.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric"
      });
    } else {
      tag = date.toLocaleDateString(undefined, {
        year: "numeric"
      });
    }

    keyPoints.push({
      index: pointIndex,
      tag: tag,
      numericTag: numericTag,
      value
    });
    if (axisSignificantMarkers.length > 0) axisSignificantMarkers[axisSignificantMarkers.length - 1].end = pointIndex - 1;
    if (!axisSignificantMarkers.some(marker => marker.numericTag === numericTag)) axisSignificantMarkers.push({
      tag,
      numericTag,
      start: pointIndex
    });
  }
  /*
  "data": {
                  "minY": -100,
                  "maxY": 110,
                  "sets": {
                      "green": {
                          "label": "Set 1",
                          "value": 48,
                          "color": "green"
                      },
                      "red": {
                          "label": "Set 2",
                          "value": 12,
                          "color": "red"
                      },
                  },
                  "points": [
                      {
                          "x": "2023-01-01",
                          "set": "green",
                          "bandTop": 48,
                          "valueTop": 40,
                          "valueBottom": 34,
                          "bandBottom": 30,
                      },
                  ]
              }
  */


  let x = columnWidth;

  for (let i = 0; i < points.length; i++) {
    const point = points[i]; // draw band as line in the center, then value as a rectangle

    const bandStart = height - (point.bandTop - minY) / (maxY - minY) * height;
    const bandEnd = height - (point.bandBottom - minY) / (maxY - minY) * height;
    ctx.beginPath();
    ctx.strokeStyle = sets[point.set].color;
    ctx.lineWidth = 3 * dpi;
    ctx.moveTo(x, bandStart);
    ctx.lineTo(x, bandEnd);
    ctx.stroke();
    const valueTop = height - (point.valueTop - minY) / (maxY - minY) * height;
    const valueBottom = height - (point.valueBottom - minY) / (maxY - minY) * height;
    ctx.fillStyle = sets[point.set].color;
    ctx.fillRect(x - columnWidth / 2, valueTop, columnWidth, valueBottom - valueTop);
    x += columnWidth * 2;
    const date = dateStringToDate(point.x);
    let numericTag;

    if (xGridPrecisionMode === "day") {
      numericTag = date.getDay();
      if (numericTag === 0) numericTag = 7;
    } else if (xGridPrecisionMode === "date") {
      numericTag = date.getDate();
    } else if (xGridPrecisionMode === "month") {
      numericTag = date.getMonth() + date.getFullYear() * 12;
    } else if (xGridPrecisionMode === "month/year") {
      numericTag = date.getMonth() + date.getFullYear() * 12;
    } else {
      numericTag = date.getFullYear();
    }

    if (keyPoints.length === 0 || numericTag != keyPoints[keyPoints.length - 1].numericTag) {
      addKeyPoint(i, point, numericTag);
    }
  }

  if (axisSignificantMarkers.length > 0) axisSignificantMarkers[axisSignificantMarkers.length - 1].end = points.length - 1; // clean up and sort the axisSignificantMarkersSet

  axisSignificantMarkers = axisSignificantMarkers.sort((a, b) => a.numericTag - b.numericTag); // adapt the axisSignificantMarkersSet to fit the width of the canvas

  while (axisSignificantMarkers.reduce((acc, marker) => acc + measureText(marker.tag).width + 10, 0) > rect.width) {
    // remove every even element and extend previous element end value
    let removedCount = 0;

    for (let i = 0; i < axisSignificantMarkers.length; i++) {
      // starts from zero = so we need to remove odd items in the array
      if ((i + removedCount) % 2 !== 0) {
        if (i > 0) axisSignificantMarkers[i - 1].end = axisSignificantMarkers[i].end;
        axisSignificantMarkers.splice(i, 1);
        removedCount++;
        i--;
      }
    }

    if (removedCount === 0) {
      axisSignificantMarkers = [];
      break;
    }
  } // render x axis


  for (let marker of axisSignificantMarkers) {
    const el = document.createElement("div");
    el.classList.add("chart-axis-label");
    el.textContent = String(marker.tag);
    el.style.width = `${(marker.end - marker.start + 1) / points.length * 100}%`;
    axisX.appendChild(el);
  }

  return canvas;
}